import React, { FunctionComponent } from 'react';

import { compact, filter, isEqual, map, uniqBy } from 'lodash';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { Autocomplete, Checkbox, TextField, Typography } from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';

import { Dropdown, ModelInstance, RowIdentifier } from '../../types/models';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface IDimensionInstancesMultiSelectProps {
  /**
   * List of dimensions selected
   */
  preSelectedDimensionInstances: RowIdentifier[];
  /**
   * The onChange handler
   * @param dimension
   */
  handleDimensionInstanceChange: (dimension: RowIdentifier[]) => void;
  /**
   * The model instance
   * This component will be disabled until a modelInstance is passed in
   */
  modelInstance?: ModelInstance;
  /**
   * List of dimension instances
   */
  dimensionInstances: RowIdentifier[];
  /**
   * Show a large component
   */
  size?: 'small' | 'medium';
  hasError?: boolean;
  disabled?: boolean;
  selectAll?: boolean;
}

const DimensionInstancesMultiSelect: FunctionComponent<
  IDimensionInstancesMultiSelectProps
> = ({
  preSelectedDimensionInstances = [],
  handleDimensionInstanceChange,
  dimensionInstances,
  modelInstance,
  size = 'small',
  hasError,
  disabled,
  selectAll = false,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const getSelectAllOption = () => {
    return {
      Name: Dropdown.Select_All,
      DimNumber: null,
      DisableInputEdit: false,
      DisableOutputAggregation: false,
      FormatKey: 0,
      GroupID: undefined,
      InputCategory: '',
      ModelInstanceID: modelInstance?.id || 0,
      PreviousGroupName: null,
      TabName: '',
      Type: 'Input' as const,
      created_at: '',
      id: 999999,
      value: 9999999,
    };
  };

  const filterOptions = (
    options: RowIdentifier[],
    { inputValue }: { inputValue: string }
  ) => {
    if (inputValue === '') {
      return options;
    }
    return options.filter((option) =>
      option.Name.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const handleChange = (
    event: React.ChangeEvent<{}>,
    value: RowIdentifier[]
  ) => {
    const target = event.target as HTMLElement;
    const isSelectAllClick =
      target.textContent === Dropdown.Select_All ||
      target.closest('li')?.textContent === Dropdown.Select_All;

    if (
      isSelectAllClick &&
      preSelectedDimensionInstances?.length !== dimensionInstances.length &&
      preSelectedDimensionInstances?.length !==
        filterOptions(dimensionInstances, { inputValue: searchTerm }).length
    ) {
      let filteredInputs = filterOptions(dimensionInstances, {
        inputValue: searchTerm,
      });
      let options = [];
      if (preSelectedDimensionInstances && searchTerm !== '') {
        options = uniqBy(
          [...preSelectedDimensionInstances, ...filteredInputs],
          'Name'
        );
        handleDimensionInstanceChange(options);
      } else {
        options = uniqBy([...filteredInputs], 'Name');
        handleDimensionInstanceChange(filteredInputs);
      }
      setSearchTerm('');
    } else if (
      isSelectAllClick &&
      preSelectedDimensionInstances?.length === dimensionInstances.length
    ) {
      handleDimensionInstanceChange([]);
    } else {
      let newValue = filter(value, (v) => v.Name !== Dropdown.Select_All);
      handleDimensionInstanceChange(newValue);
    }
  };

  const getSelectedFlag = (selected: boolean) => {
    if (preSelectedDimensionInstances?.length === dimensionInstances.length) {
      return true;
    } else {
      return selected;
    }
  };

  const setSearchString = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const searchString = target.value;
    setSearchTerm(searchString);
  };

  return (
    <Autocomplete<RowIdentifier, true>
      limitTags={1}
      multiple
      value={preSelectedDimensionInstances}
      onChange={(event, value) => handleChange(event, value)}
      disableCloseOnSelect
      ChipProps={{
        size: 'small',
      }}
      options={dimensionInstances || []}
      getOptionLabel={(option) => option.Name}
      isOptionEqualToValue={(option, value) => isEqual(option.id, value.id)}
      filterOptions={(filteredOptions, params) => {
        const filtered = filterOptions(filteredOptions, params);
        let selectAllOption = null;
        if (selectAll && filtered.length > 0) {
          selectAllOption = getSelectAllOption();
        }

        return compact([selectAllOption, ...filtered]) as RowIdentifier[];
      }}
      renderOption={(props, option, { inputValue, selected }) => {
        const matches = match(option.Name, inputValue, {
          insideWords: true,
          findAllOccurrences: true,
        });
        const parts = parse(option.Name, matches);
        return (
          <Typography component="li" {...props} noWrap>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selectAll ? getSelectedFlag(selected) : selected}
            />
            {map(parts, (part, index) => (
              <span
                key={index}
                style={{
                  fontWeight: part.highlight ? 700 : 400,
                  whiteSpace: 'pre',
                }}
              >
                {part.text}
              </span>
            ))}
          </Typography>
        );
      }}
      disabled={modelInstance === undefined || disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Dimension Instances"
          fullWidth
          size={size}
          error={hasError}
          onInput={setSearchString}
        />
      )}
    />
  );
};

export default DimensionInstancesMultiSelect;
